// src/components/personal/sections/ClientsSection/components/ClientCard/ClientCard.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Client } from '../../types/types';
import { TEXT_STYLES } from '@/theme/typography';
import { COLORS } from '@/theme';

// Импортируем компоненты модального окна из нашей UI-библиотеки
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';

// Заменяем useDeviceType на useResponsive
import { useResponsive } from '@/hooks/useResponsive';

// Добавляем стили для скрытия скроллбара при сохранении функциональности прокрутки
const noScrollbarStyles = `
.no-scrollbar {
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox */
}
.no-scrollbar::-webkit-scrollbar { 
  display: none;  /* Safari and Chrome */
}
`;

import ClientInfo from './ClientInfo';
import ClientChart from './ClientChart';
import ClientProducts from './ClientProducts';
import ClientOrders from './ClientOrders';
import { getStatusTextStatic } from '../../utils/statusHelpers';
import Message from '@/components/ui/Message';
import { useClientInfo } from '../../hooks/useClientInfo';

interface ClientCardProps {
  client: Client;
  onClose: () => void;
  disabled: boolean;
}

type TabType = 'general' | 'products' | 'orders';

const ClientCard: React.FC<ClientCardProps> = ({ client, onClose, disabled }) => {
  const { t } = useTranslation(['clients', 'common']);
  const [activeTab, setActiveTab] = useState<TabType>('general');
  
  // Заменяем useDeviceType на useResponsive
  const { isMobile } = useResponsive();
  
  // Используем хук для получения детальной информации о клиенте
  const { 
    clientInfo,
    isLoading,
    error,
    setClientId
  } = useClientInfo();

  // При монтировании компонента устанавливаем ID клиента
  useEffect(() => {
    setClientId(client.id);
    
    // При размонтировании сбрасываем ID клиента
    return () => {
      setClientId(null);
    };
  }, [client.id, setClientId]);

  const tabs: { id: TabType; label: string }[] = [
    { id: 'general', label: t('card.tabs.general') },
    { id: 'products', label: t('card.tabs.products') },
    { id: 'orders', label: t('card.tabs.orders') },
  ];

  // Стиль для кнопок вкладок с адаптивными размерами
  const tabButtonClass = (isActive: boolean) => `
    px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-colors duration-200 flex-shrink-0
    ${isMobile ? 'text-sm' : TEXT_STYLES.small}
    ${isActive 
      ? 'bg-primary/10 text-primary' 
      : 'text-text hover:bg-background'
    }
  `;

  // Использование новых компонентов модального окна
  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size={isMobile ? "full" : "xl"}
      mobileMode={isMobile ? "fullscreen" : "normal"}
      draggable={!isMobile}
      swipeToDismiss={isMobile}
      className={isMobile ? "safe-area-padding" : ""}
    >
      {/* Внедряем стили для скрытия скроллбара и безопасной области */}
      <style>{`
        ${noScrollbarStyles}
        
        /* Дополнительные стили для учета безопасной области мобильных браузеров */
        .safe-area-padding {
          padding-top: env(safe-area-inset-top, 20px);
        }
        
        /* Увеличение размера области захвата на мобильных устройствах */
        @media (max-width: 640px) {
          .modal-close-button {
            width: 44px !important;
            height: 44px !important;
            top: env(safe-area-inset-top, 10px);
            right: 10px;
          }
        }
      `}</style>
      {/* Заголовок модального окна с дополнительным отступом для мобильных устройств */}
      <ModalHeader onClose={onClose} className={isMobile ? "pt-8 sm:pt-4" : ""}>
        <div className="flex items-center flex-wrap">
          <span className="mr-2">{t('card.title', { id: client.id })}</span>
          {client.status && (
            <span className={`px-2 py-1 rounded text-xs ${COLORS.clientStatus[client.status].text} ${COLORS.clientStatus[client.status].bg}`}>
              {getStatusTextStatic(client.status, t)}
            </span>
          )}
        </div>
      </ModalHeader>

      {/* Вкладки */}
      <div className="px-3 py-2 sm:px-6 sm:py-2 border-b border-secondary/20">
        <div className="flex overflow-x-auto no-scrollbar" style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${tabButtonClass(activeTab === tab.id)} mr-2`}
              disabled={disabled || isLoading}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Тело модального окна */}
      <ModalBody scrollable={true} className="mt-2">
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
            <span>{t('common:loading')}</span>
          </div>
        )}
        
        {error && (
          <Message
            type="error"
            message={error}
          />
        )}
        
        {!isLoading && !error && clientInfo && (
          <>
            {activeTab === 'general' && (
              <div className="space-y-4"> {/* Уменьшаем отступы между компонентами */}
                <ClientInfo
                  client={client}
                  clientInfo={clientInfo}
                />
                {/* Выделяем больше пространства для графика */}
                <div className="mt-2 mb-0 pb-0">
                  <ClientChart ordersByMonth={clientInfo.orders_by_month} />
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <ClientProducts products={clientInfo.products} />
            )}

            {activeTab === 'orders' && (
              <ClientOrders orders={clientInfo.orders} />
            )}
          </>
        )}
      </ModalBody>

      {/* Футер модального окна */}
      <ModalFooter mobileFlex="auto">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700 transition-colors"
          disabled={disabled || isLoading}
        >
          {t('common:close')}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientCard;